import React, { useState, useEffect, useRef } from "react";
import { Eye, Target, Quote, Award, ChevronRight, Play, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const slideLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const slideRight = {
  hidden: { opacity: 0, x: 80 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

// ✅ REUSABLE MODAL COMPONENT - No more duplication!
const Modal = ({ isOpen, onClose, title, children, type = "video" }) => {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      setTimeout(() => closeButtonRef.current?.focus(), 100);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <motion.div
            className={`relative bg-white rounded-3xl p-4 sm:p-6 w-full ${
              type === "video" ? "max-w-4xl" : "max-w-3xl max-h-[85vh] overflow-y-auto"
            }`}
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              ref={closeButtonRef}
              onClick={onClose}
              aria-label="Close modal"
              className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center z-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>

            <h3 id="modal-title" className="text-lg sm:text-xl font-bold text-gray-900 mb-4 pr-10">
              {title}
            </h3>
            {children}
            <p className="text-sm text-gray-600 mt-4 text-center">
              Press ESC or click outside to close
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ✅ REUSABLE IMAGE CARD COMPONENT
const ImageCard = ({ src, alt, onPlayClick, playButtonColor = "blue", imageLoaded, setImageLoaded, imageError, setImageError }) => {
  return (
    <div className="relative bg-white/80 backdrop-blur-xl border border-gray-100 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-4 sm:p-6">
      <div className="relative overflow-hidden rounded-2xl">
        {/* Loading skeleton */}
        {!imageLoaded && !imageError && (
          <div className="w-full aspect-[4/3] bg-gray-200 rounded-2xl animate-pulse" />
        )}
        
        {/* Error fallback */}
        {imageError && (
          <div className="w-full aspect-[4/3] bg-gray-100 rounded-2xl flex items-center justify-center">
            <p className="text-gray-500 text-sm">Image not available</p>
          </div>
        )}

        <img
          src={src}
          alt={alt}
          className={`w-full aspect-[4/3] object-cover rounded-2xl transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />

        {imageLoaded && !imageError && (
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-2xl" />

            <button
              onClick={onPlayClick}
              aria-label={`Play ${alt.toLowerCase()} video`}
              className="absolute inset-0 flex items-center justify-center group/play focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-2xl"
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:shadow-2xl transition group-hover/play:scale-110">
                <Play className={`w-6 h-6 sm:w-8 sm:h-8 text-${playButtonColor}-600 ml-1`} />
              </div>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const VisionMission = () => {
  // ✅ Simplified state management
  const [activeModal, setActiveModal] = useState(null);
  const [visionImageLoaded, setVisionImageLoaded] = useState(false);
  const [visionImageError, setVisionImageError] = useState(false);
  const [missionImageLoaded, setMissionImageLoaded] = useState(false);
  const [missionImageError, setMissionImageError] = useState(false);
  const [specialImageLoaded, setSpecialImageLoaded] = useState(false);
  const [specialImageError, setSpecialImageError] = useState(false);

  const visionDetails = `Our vision represents our commitment to excellence in technical education. We strive to be a leading institution that produces graduates who are not only technically competent but also morally upright and entrepreneurially minded.

This vision guides our approach to curriculum development, faculty recruitment, infrastructure development, and student engagement. We believe in creating an environment where innovation thrives and students are prepared for the challenges of the modern world.

Through our comprehensive programs and dedicated faculty, we aim to maintain the highest standards of technical education while fostering creativity, critical thinking, and ethical leadership among our students.`;

  const missionDetails = `Our mission is centered around the "Bida Standard" - a mark of excellence that represents quality, integrity, and innovation in technical education. We are committed to building an enabling campus environment that supports learning, research, and development.

We focus on practical, hands-on training that prepares our students for immediate contribution to industry and society. Our programs are designed to bridge the gap between theoretical knowledge and practical application.

Through partnerships with industry, continuous curriculum updates, and investment in modern facilities, we ensure that our graduates are well-prepared for the evolving demands of the global marketplace.`;
const specialDetails = ` The office of the Deputy Rector (Special Duties) plays a strategic role in advancing the institution’s mission and vision through specialized initiatives  that strengthen operational efficiency, staff and student welfare, campus security, innovation, and community engagement.
Our commitment is to support every arm of the institution by coordinating special projects, driving reforms, improving service delivery, and ensuring that the Polytechnic remains a model of discipline, integrity, and excellence.
We champion initiatives that enhance safety, promote digital transformation, empower staff development, and expand the institution’s partnerships. Through proactive leadership, we ensure that the Federal Polytechnic Bida continues to uphold the “Bida Standard”—a symbol of quality, innovation, and sustainable institutional growth.`;

  return (
    <section 
      className="relative w-full py-12 sm:py-16 md:py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 overflow-hidden"
      aria-labelledby="vision-mission-heading"
    >
      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-8 sm:mb-12 md:mb-16"
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-5">
            <Award className="w-4 h-4 mr-2" />
            Our Foundation
          </div>
          <h2 
            id="vision-mission-heading"
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 leading-tight px-4"
          >
            Our{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Vision & Mission
            </span>
          </h2>
        </motion.div>

        <div className="space-y-12 md:space-y-20">
          {/* Vision Section */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Vision Image Card */}
            <motion.div
              className="relative group w-full"
              variants={slideLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <div className="absolute -top-8 -left-8 w-36 h-36 bg-blue-200 rounded-full opacity-20 group-hover:opacity-30 transition" aria-hidden="true" />
              <div className="absolute -bottom-8 -right-8 w-28 h-28 bg-purple-200 rounded-full opacity-20 group-hover:opacity-30 transition" aria-hidden="true" />

              <ImageCard
                src="/images/AR.png"
                alt="Dr. A. A. Shehu, Deputy Rector Academic"
                onPlayClick={() => setActiveModal('visionVideo')}
                playButtonColor="blue"
                imageLoaded={visionImageLoaded}
                setImageLoaded={setVisionImageLoaded}
                imageError={visionImageError}
                setImageError={setVisionImageError}
              />
            </motion.div>

            {/* Vision Content */}
            <motion.div
              className="relative w-full"
              variants={slideRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <Quote className="absolute -top-6 -left-6 w-10 h-10 sm:w-14 sm:h-14 text-blue-100" aria-hidden="true" />
              <div className="relative z-10 bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8 md:p-10 hover:shadow-2xl transition">
                <div className="flex items-center justify-center md:justify-start space-x-3 mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg" aria-hidden="true">
                    <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-700">
                    Our Vision
                  </h3>
                </div>

                <blockquote className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-6 sm:mb-8">
                  A leading Polytechnic with enviable and excellent academic
                  standard, training students to be technologically skilled,
                  morally sound, and entrepreneurial.
                </blockquote>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-200">
                  <div className="flex-1">
                    <p className="font-bold text-gray-900 text-sm sm:text-base">Dr. A. A. Shehu</p>
                    <p className="font-bold text-gray-900 text-sm sm:text-base">Deputy Rector Academic</p>
                    <p className="text-gray-600 text-xs sm:text-sm">Excellence in Education & Building Future Leaders</p>
                  </div>
                  <button
                    onClick={() => setActiveModal('visionDetails')}
                    aria-label="Learn more about our vision"
                    className="inline-flex items-center justify-center px-4 sm:px-5 py-2 rounded-full bg-blue-50 text-blue-700 font-semibold text-sm hover:bg-blue-100 transition whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Know More
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Mission Section */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Mission Content */}
            <motion.div
              className="relative w-full order-2 lg:order-1"
              variants={slideLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <Quote className="absolute -top-6 -right-6 w-10 h-10 sm:w-14 sm:h-14 text-green-100" aria-hidden="true" />
              <div className="relative z-10 bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8 md:p-10 hover:shadow-2xl transition">
                <div className="flex items-center justify-center md:justify-start space-x-3 mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-gradient-to-r from-green-500 to-green-700 shadow-lg" aria-hidden="true">
                    <Target className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-green-700">
                    Our Mission
                  </h3>
                </div>

                <blockquote className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-6 sm:mb-8">
                  To build an enabling campus environment and train students in
                  technology and management through the earned 'Bida Standard' for
                  sustainable development.
                </blockquote>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-200">
                  <div className="flex-1">
                    <p className="font-bold text-gray-900 text-sm sm:text-base">Engr. Dr. Saba Ahmed Mohammed</p>
                    <p className="font-bold text-gray-900 text-sm sm:text-base">Deputy Rector Admin.</p>
                    <p className="text-gray-600 text-xs sm:text-sm">The Bida Standard Quality & Innovation</p>
                  </div>
                  <button
                    onClick={() => setActiveModal('missionDetails')}
                    aria-label="Learn more about our mission"
                    className="inline-flex items-center justify-center px-4 sm:px-5 py-2 rounded-full bg-green-50 text-green-700 font-semibold text-sm hover:bg-green-100 transition whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    Know More
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Special Image Card */}
            <motion.div
              className="relative group w-full order-1 lg:order-2"
              variants={slideRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <div className="absolute -top-8 -left-8 w-36 h-36 bg-purple-200 rounded-full opacity-20 group-hover:opacity-30 transition" aria-hidden="true" />
              <div className="absolute -bottom-8 -right-8 w-28 h-28 bg-pink-200 rounded-full opacity-20 group-hover:opacity-30 transition" aria-hidden="true" />

              <ImageCard
                src="/images/henry.png"
                alt="Engr. Dr. Saba Ahmed Mohammed, Deputy Rector Administration"
                onPlayClick={() => setActiveModal('missionVideo')}
                playButtonColor="green"
                imageLoaded={missionImageLoaded}
                setImageLoaded={setMissionImageLoaded}
                imageError={missionImageError}
                setImageError={setMissionImageError}
              />
            </motion.div>
          </div>

          {/* Mission Section */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Mission Content */}
            <motion.div
              className="relative w-full order-2 lg:order-1"
              variants={slideRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <Quote className="absolute -top-6 -right-6 w-10 h-10 sm:w-14 sm:h-14 text-purple-100" aria-hidden="true" />
              <div className="relative z-10 bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8 md:p-10 hover:shadow-2xl transition">
                <div className="flex items-center justify-center md:justify-start space-x-3 mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-purple-700 shadow-lg" aria-hidden="true">
                    <Target className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-purple-700">
                    Our Commitment to Institutional Excellence
                  </h3>
                </div>

                <blockquote className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-6 sm:mb-8">
                  “We strengthen the institution through special initiatives that enhance welfare, security, innovation, and operational
                </blockquote>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-200">
                  <div className="flex-1">
                    <p className="font-bold text-gray-900 text-sm sm:text-base">Surv. Elijah O. Alamu</p>
                    <p className="font-bold text-gray-900 text-sm sm:text-base">Deputy Rector Special Duties.</p>
                    <p className="text-gray-600 text-xs sm:text-sm">Driving Innovation, Safety & Institutional Growth</p>
                  </div>
                  <button
                    onClick={() => setActiveModal('specialDetails')}
                    aria-label="Learn more about our mission"
                    className="inline-flex items-center justify-center px-4 sm:px-5 py-2 rounded-full bg-purple-50 text-purple-700 font-semibold text-sm hover:bg-purple-100 transition whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                  >
                    Learn more about Special Duties
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Mission Image Card */}
            <motion.div
              className="relative group w-full order-1 lg:order-2"
              variants={slideLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <div className="absolute -top-8 -left-8 w-36 h-36 bg-purple-200 rounded-full opacity-20 group-hover:opacity-30 transition" aria-hidden="true" />
              <div className="absolute -bottom-8 -right-8 w-28 h-28 bg-yellow-200 rounded-full opacity-20 group-hover:opacity-30 transition" aria-hidden="true" />

              <ImageCard
                src="/images/duty.png"
                alt="Surv. Elijah O. Alamu, Deputy Rector Special Duties"
                onPlayClick={() => setActiveModal('specialVideo')}
                playButtonColor="brown"
                imageLoaded={specialImageLoaded}
                setImageLoaded={setSpecialImageLoaded}
                imageError={specialImageError}
                setImageError={setSpecialImageError}
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* ✅ SINGLE MODAL COMPONENT - Handles all 4 cases */}
      <Modal
        isOpen={activeModal === 'visionVideo'}
        onClose={() => setActiveModal(null)}
        title="Our Vision in Action"
        type="video"
      >
        <div className="aspect-video rounded-2xl overflow-hidden bg-gray-900">
          <iframe
            src={activeModal === 'visionVideo' ? "https://www.youtube.com/embed/YOUR_VISION_VIDEO_ID" : ""}
            title="Vision Video - Federal Polytechnic Bida"
            className="w-full h-full"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </Modal>

      <Modal
        isOpen={activeModal === 'missionVideo'}
        onClose={() => setActiveModal(null)}
        title="Our Mission in Practice"
        type="video"
      >
        <div className="aspect-video rounded-2xl overflow-hidden bg-gray-900">
          <iframe
            src={activeModal === 'missionVideo' ? "https://www.youtube.com/embed/YOUR_MISSION_VIDEO_ID" : ""}
            title="Mission Video - Federal Polytechnic Bida"
            className="w-full h-full"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </Modal>


      <Modal
        isOpen={activeModal === 'specialVideo'}
        onClose={() => setActiveModal(null)}
        title="Special Duties in Action"
        type="video"
      >
        <div className="aspect-video rounded-2xl overflow-hidden bg-gray-900">
          <iframe
            src={activeModal === 'specialVideo' ? "https://www.youtube.com/embed/YOUR_MISSION_VIDEO_ID" : ""}
            title="Special Duties Video - Federal Polytechnic Bida"
            className="w-full h-full"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </Modal>

      <Modal
        isOpen={activeModal === 'visionDetails'}
        onClose={() => setActiveModal(null)}
        title="Our Vision - Excellence in Education"
        type="details"
      >
        <p className="text-justify text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">
          {visionDetails}
        </p>
      </Modal>

      <Modal
        isOpen={activeModal === 'missionDetails'}
        onClose={() => setActiveModal(null)}
        title="Our Mission - The Bida Standard"
        type="details"
      >
        <p className="text-justify text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">
          {missionDetails}
        </p>
      </Modal>

      <Modal
        isOpen={activeModal === 'specialDetails'}
        onClose={() => setActiveModal(null)}
        title="Our Mission - The Bida Standard"
        type="details"
      >
        <p className="text-justify text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">
          {specialDetails}
        </p>
      </Modal>
    </section>
  );
};

export default VisionMission;
